/**
 * Token Validation Utilities
 *
 * Validates JWT tokens before decoding to prevent app crashes
 * from malformed or corrupted tokens.
 * 
 * Implementation: Step 6 - Add Token Validation
 */

import debug from 'debug';

const debugValidation = debug('impl:step6:validation');
const debugError = debug('impl:step6:error');

/**
 * Error codes for token validation failures
 * Used to distinguish between different types of validation errors
 */
export const TOKEN_ERROR_CODES = Object.freeze({
  EXPIRED: 'EXPIRED',           // Token has expired (exp < now)
  INVALID: 'INVALID',           // Token structure/format is invalid
  MISSING_CLAIM: 'MISSING_CLAIM', // Required claim is missing
});

/**
 * Custom error class for token validation failures
 *
 * @property {string} field - Which field failed validation
 * @property {string} code - Error code from TOKEN_ERROR_CODES
 * @property {object} metadata - Additional context (e.g., expiration times)
 */
export class TokenValidationError extends Error {
  constructor(message, field, code = TOKEN_ERROR_CODES.INVALID, metadata = {}) {
    super(message);
    this.name = 'TokenValidationError';
    this.field = field; // Which field failed validation
    this.code = code;   // Error code for categorization
    this.metadata = metadata; // Additional context for debugging/logging
  }
}

/**
 * Validate JWT structure before decoding
 * Step 6 Integration Point 1: Write to storage validation
 *
 * @param {string} token - JWT token string
 * @returns {boolean} true if valid
 * @throws {TokenValidationError} if invalid
 */
export function validateTokenStructure(token) {
  debugValidation('step 6.1: validating JWT structure', { 
    tokenLength: token?.length,
    tokenType: typeof token 
  });

  if (!token || typeof token !== 'string') {
    debugError('step 6.1: token validation failed - not a string', {
      tokenLength: token?.length,
      type: typeof token
    });
    throw new TokenValidationError('Token must be a non-empty string', 'token');
  }

  // JWT must have 3 parts: header.payload.signature
  const parts = token.split('.');
  debugValidation('step 6.1: checking JWT parts', { 
    partsCount: parts.length,
    expectedParts: 3 
  });

  if (parts.length !== 3) {
    debugError('step 6.1: invalid JWT structure - wrong part count', {
      expected: 3,
      actual: parts.length,
      tokenLength: token.length
    });
    throw new TokenValidationError(
      `Invalid JWT structure (expected 3 parts, got ${parts.length})`,
      'token'
    );
  }

  // Each part should be base64url encoded (non-empty)
  for (let i = 0; i < parts.length; i++) {
    if (!parts[i] || parts[i].length === 0) {
      debugError('step 6.1: JWT part is empty', { 
        partIndex: i + 1,
        partName: ['header', 'payload', 'signature'][i] 
      });
      throw new TokenValidationError(
        `JWT part ${i + 1} is empty`,
        'token'
      );
    }
  }

  debugValidation('step 6.1: JWT structure validation passed', { 
    headerLength: parts[0].length,
    payloadLength: parts[1].length,
    signatureLength: parts[2].length 
  });
  return true;
}

/**
 * Validate decoded token has required claims
 * Step 6 Integration Point 2: Validate claims after decode
 *
 * @param {object} decoded - Decoded JWT payload
 * @returns {boolean} true if valid
 * @throws {TokenValidationError} if invalid
 */
export function validateDecodedToken(decoded) {
  debugValidation('step 6.2: validating decoded token claims', { 
    hasDecoded: !!decoded,
    type: typeof decoded 
  });

  if (!decoded || typeof decoded !== 'object') {
    debugError('step 6.2: decoded token is not an object', { 
      decoded,
      type: typeof decoded 
    });
    throw new TokenValidationError('Decoded token must be an object', 'decoded');
  }

  // Required claims for this application (using actual token field names)
  const required = ['exp', 'Id', 'UserName'];
  const presentClaims = Object.keys(decoded);
  
  debugValidation('step 6.2: checking required claims', { 
    required,
    present: presentClaims.length,
    claims: presentClaims 
  });

  for (const field of required) {
    if (!(field in decoded)) {
      debugError('step 6.2: missing required claim', {
        missingField: field,
        requiredFields: required,
        presentFields: presentClaims
      });
      throw new TokenValidationError(
        `Missing required claim: ${field}`,
        field,
        TOKEN_ERROR_CODES.MISSING_CLAIM
      );
    }
  }

  // Validate expiration is a valid Unix timestamp
  debugValidation('step 6.2: validating expiration timestamp', { 
    exp: decoded.exp,
    expType: typeof decoded.exp 
  });

  if (typeof decoded.exp !== 'number' || decoded.exp <= 0) {
    debugError('step 6.2: invalid expiration timestamp', { 
      exp: decoded.exp,
      type: typeof decoded.exp 
    });
    throw new TokenValidationError(
      'Invalid expiration timestamp (must be positive number)',
      'exp'
    );
  }

  // Check if token is already expired
  const now = Math.floor(Date.now() / 1000);
  const timeToExpiry = decoded.exp - now;
  
  debugValidation('step 6.2: checking token expiration', { 
    exp: decoded.exp,
    now,
    timeToExpirySeconds: timeToExpiry,
    isExpired: timeToExpiry < 0 
  });

  if (decoded.exp < now) {
    const expiredBySeconds = now - decoded.exp;
    const expUTC = new Date(decoded.exp * 1000).toISOString();
    const nowUTC = new Date(now * 1000).toISOString();
    debugError('step 6.2: token is expired', {
      exp: decoded.exp,
      now,
      expiredBy: expiredBySeconds,
      userId: decoded.Id
    });
    throw new TokenValidationError(
      `Token is expired (exp: ${decoded.exp} [${expUTC}], now: ${now} [${nowUTC}])`,
      'exp',
      TOKEN_ERROR_CODES.EXPIRED,
      {
        tokenExpiration: decoded.exp,
        tokenExpirationISO: expUTC,
        currentTime: now,
        currentTimeISO: nowUTC,
        expiredBySeconds,
        userId: decoded.Id,
        userName: decoded.UserName,
      }
    );
  }

  debugValidation('step 6.2: decoded token validation passed', { 
    userId: decoded.Id,
    userName: decoded.UserName,
    timeToExpiry: timeToExpiry 
  });
  return true;
}
